import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, PencilIcon, XIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { whenLabel, type MemoryFile, type MemoryVersionEntry } from '@/pages/assistant/shared';

/** One memory, read-only, at its own URL; editing lives on the /edit route. */
export function MemoryViewPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memoryId = useParams<{ memoryId: string }>().memoryId ?? '';
  const [deleting, setDeleting] = useState(false);
  const [viewing, setViewing] = useState<MemoryVersionEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const memory = useQuery({
    queryKey: ['assistant-memory', memoryId],
    queryFn: ({ signal }) => getJson<MemoryFile>('/assistant/memories/file', { id: memoryId }, signal),
  });
  const versions = useQuery({
    queryKey: ['assistant-memory-versions', memoryId],
    queryFn: ({ signal }) =>
      getJson<MemoryVersionEntry[]>('/assistant/memories/versions', { id: memoryId }, signal),
  });

  const remove = useMutation({
    mutationFn: (path: string) =>
      sendJson('delete', `/assistant/memories/file?path=${encodeURIComponent(path)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-memories'] });
      navigate('/assistant/memory');
    },
    onError: (error) => {
      setDeleting(false);
      setNotice(errorMessage(error));
    },
  });

  if (memory.isPending) return <LoadingState label="Loading memory…" />;
  if (memory.isError) {
    return <ErrorNotice title="Memory not found" message={errorMessage(memory.error)} />;
  }
  const file = memory.data;

  return (
    <section className="animate-portal-rise">
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title={file.description || file.path}
          subtitle={`${file.path} · v${file.version}${whenLabel(file.updatedAt) ? ` · ${whenLabel(file.updatedAt)}` : ''}`}
        />
        <span className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/assistant/memory')}>
            <ArrowLeftIcon aria-hidden className="size-4" /> All memory
          </Button>
          <Button variant="secondary" onClick={() => setDeleting(true)}>
            <XIcon aria-hidden className="size-4" /> Forget
          </Button>
          <Button onClick={() => navigate(`/assistant/memory/${memoryId}/edit`)}>
            <PencilIcon aria-hidden className="size-4" /> Edit
          </Button>
        </span>
      </div>

      {notice === null ? null : (
        <div className="mb-3">
          <ErrorNotice inline title="Something went wrong" message={notice} />
        </div>
      )}

      <Panel className="p-5">
        {(versions.data?.length ?? 0) > 1 ? (
          <div className="mb-4 flex justify-end">
            <label className="flex items-center gap-2 text-portal-micro font-medium text-portal-muted">
              Version
              <select
                value={viewing?.version ?? file.version}
                onChange={(event) => {
                  const selected = Number(event.target.value);
                  setViewing(
                    selected === file.version
                      ? null
                      : ((versions.data ?? []).find((entry) => entry.version === selected) ?? null),
                  );
                }}
                className="rounded-md border border-admin-line px-2 py-1 text-portal-small text-portal-ink focus:border-brand/60 focus:outline-none"
              >
                <option value={file.version}>v{file.version} (current)</option>
                {(versions.data ?? [])
                  .filter((entry) => entry.version !== file.version)
                  .map((entry) => (
                    <option key={entry.version} value={entry.version}>
                      v{entry.version} — {entry.updatedBy}
                      {whenLabel(entry.createdAt) ? ` · ${whenLabel(entry.createdAt)}` : ''}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        ) : null}
        {viewing !== null ? (
          <p className="mb-3 rounded-md bg-portal-sky px-3 py-2 text-portal-micro font-medium text-brand">
            Viewing v{viewing.version} (read-only, written by {viewing.updatedBy}) — the current
            version is v{file.version}.
          </p>
        ) : null}
        <pre className="rounded-md border border-admin-line bg-portal-bg p-3 font-mono text-portal-micro leading-relaxed whitespace-pre-wrap">
          {(viewing ?? file).content}
        </pre>
        {viewing !== null ? (
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setViewing(null)}>
              Back to current
            </Button>
            <Button
              onClick={() =>
                navigate(`/assistant/memory/${memoryId}/edit`, { state: { restore: viewing } })
              }
            >
              Restore this version
            </Button>
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={deleting}
        tone="danger"
        title="Forget this memory?"
        message={`"${file.description || file.path}" will be permanently removed.`}
        confirmLabel="Forget it"
        onConfirm={() => remove.mutate(file.path)}
        onCancel={() => {
          if (!remove.isPending) setDeleting(false);
        }}
        pending={remove.isPending}
      />
    </section>
  );
}
